// Client Portal — clients log in with phone + password to view their reports.
import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, LogOut, TrendingUp, FileText, CheckCircle2, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "slowcafe_portal_token";

const STAGE_STEPS = [
    ["new", "分析中 Analysis"], ["proposal_sent", "提案已发 Proposal"],
    ["negotiating", "洽谈中 Discussing"], ["won", "合作开始 Onboard"]];

export default function Portal() {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
    const [data, setData] = useState(null);
    const [checking, setChecking] = useState(Boolean(token));

    useEffect(() => {
        if (!token) return;
        (async () => {
            try {
                const { data: me } = await axios.get(`${API}/portal/me`, { headers: { Authorization: `Bearer ${token}` } });
                setData(me);
            } catch {
                localStorage.removeItem(TOKEN_KEY);
                setToken("");
            }
            setChecking(false);
        })();
    }, [token]);

    const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(""); setData(null); };

    if (checking) return <div className="min-h-screen bg-cafe-cream flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-cafe-olive" /></div>;
    if (!token || !data) return <PortalLogin onToken={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); setChecking(true); }} />;
    return <PortalDashboard data={data} onLogout={logout} />;
}

function PortalLogin({ onToken }) {
    const [phone, setPhone] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setError("");
        try {
            const { data } = await axios.post(`${API}/portal/auth/login`, { phone, password });
            onToken(data.token);
        } catch (err) {
            const d = err?.response?.data?.detail;
            setError(typeof d === "string" ? d : "登录失败 Login failed");
            setBusy(false);
        }
    };
    return (
        <div className="min-h-screen bg-cafe-cream flex items-center justify-center px-4" data-testid="portal-login">
            <div className="w-full max-w-sm">
                <p className="text-[11px] uppercase tracking-[0.24em] text-cafe-olive font-bold text-center">Kaily F&B Visual Studio</p>
                <h1 className="font-serif text-3xl text-cafe-espresso text-center mt-2">客户门户 <span className="text-cafe-mocha text-xl">Client Portal</span></h1>
                <p className="text-[13px] text-cafe-mocha text-center mt-2 mb-8">查看你的品牌成长报告与合作进度</p>
                <form onSubmit={submit} className="grid gap-3">
                    <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" data-testid="portal-phone-input"
                        placeholder="手机号 Phone (e.g. 0123456789)"
                        className="w-full rounded-xl border border-cafe-espresso/15 bg-white px-4 py-3 text-sm outline-none focus:border-cafe-olive" />
                    <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" data-testid="portal-password-input"
                        placeholder="密码 Password"
                        className="w-full rounded-xl border border-cafe-espresso/15 bg-white px-4 py-3 text-sm outline-none focus:border-cafe-olive" />
                    {error && <p className="text-[12.5px] text-red-700" data-testid="portal-login-error">{error}</p>}
                    <button type="submit" disabled={busy || !phone || !password} data-testid="portal-login-submit"
                        className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark disabled:opacity-50 text-cafe-cream h-11 text-sm font-medium flex items-center justify-center gap-2">
                        {busy && <Loader2 className="h-4 w-4 animate-spin" />} 登录 Log in
                    </button>
                </form>
                <p className="text-[11.5px] text-cafe-mocha/70 text-center mt-6">登录信息由 Kaily F&B Visual Studio 提供 · 如忘记密码请联系我们</p>
            </div>
        </div>
    );
}

const RATING_LABELS = [["brand", "品牌 Brand"], ["visual", "视觉 Visual"], ["content", "内容 Content"], ["trust", "信任 Trust"], ["cta", "行动号召 CTA"]];

function PortalDashboard({ data, onLogout }) {
    const ratings = data.ratings || {};
    const stageIdx = Math.max(0, STAGE_STEPS.findIndex(([s]) => s === data.pipeline_stage));
    const lost = data.pipeline_stage === "lost";
    return (
        <div className="min-h-screen bg-cafe-cream" data-testid="portal-dashboard">
            <header className="border-b border-cafe-espresso/10 bg-white/70 backdrop-blur sticky top-0 z-10">
                <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-3">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.22em] text-cafe-olive font-bold">Kaily F&B Visual Studio · Client Portal</p>
                        <h1 className="font-serif text-xl text-cafe-espresso" data-testid="portal-business-name">{data.business_name}</h1>
                    </div>
                    <button onClick={onLogout} data-testid="portal-logout" className="ml-auto flex items-center gap-1.5 text-[12px] text-cafe-mocha hover:text-cafe-espresso">
                        <LogOut className="h-3.5 w-3.5" /> 退出
                    </button>
                </div>
            </header>
            <main className="max-w-3xl mx-auto px-5 py-8 grid gap-5 pb-20">
                {/* progress */}
                {!lost && (
                    <section className="rounded-2xl border border-cafe-espresso/12 bg-white/70 p-5" data-testid="portal-progress">
                        <p className="text-[11px] uppercase tracking-[0.2em] text-cafe-olive font-bold mb-4">合作进度 · Progress</p>
                        <div className="flex items-center">
                            {STAGE_STEPS.map(([s, label], i) => (
                                <div key={s} className="flex items-center flex-1 last:flex-none">
                                    <div className="flex flex-col items-center">
                                        <div className={`h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-bold ${i <= stageIdx ? "bg-cafe-olive text-cafe-cream" : "bg-cafe-latte text-cafe-mocha"}`}>
                                            {i < stageIdx ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                                        </div>
                                        <span className="text-[10px] text-cafe-mocha mt-1.5 text-center whitespace-nowrap">{label.split(" ")[0]}</span>
                                    </div>
                                    {i < STAGE_STEPS.length - 1 && <div className={`h-0.5 flex-1 mx-1 mb-4 ${i < stageIdx ? "bg-cafe-olive" : "bg-cafe-latte"}`} />}
                                </div>))}
                        </div>
                    </section>)}
                {/* audit ratings */}
                {Object.keys(ratings).length > 0 && (
                    <section className="rounded-2xl bg-cafe-espresso text-cafe-cream p-6" data-testid="portal-score-card">
                        <p className="text-[11px] uppercase tracking-[0.2em] text-cafe-cream/60 font-bold flex items-center gap-1.5 mb-4">
                            <TrendingUp className="h-3.5 w-3.5" /> 品牌 Instagram 评分 · Ratings</p>
                        <div className="grid gap-3">
                            {RATING_LABELS.map(([k, label]) => (
                                <div key={k} className="flex items-center gap-3 text-[13.5px]">
                                    <span className="w-32 shrink-0 text-cafe-cream/70">{label}</span>
                                    <span className="text-[#E0B84C] tracking-[3px]">{"★".repeat(ratings[k] || 0)}{"☆".repeat(Math.max(0, 5 - (ratings[k] || 0)))}</span>
                                    <b className="text-[12px] text-cafe-cream/60">{ratings[k]}/5</b>
                                </div>))}
                        </div>
                    </section>)}
                {/* proposal & quote */}
                {(data.proposal_path || data.quote) && (
                    <section className="rounded-2xl border border-cafe-espresso/12 bg-white/70 p-5" data-testid="portal-docs">
                        <p className="text-[11px] uppercase tracking-[0.2em] text-cafe-olive font-bold mb-4 flex items-center gap-1.5">
                            <FileText className="h-3.5 w-3.5" /> 你的提案与报价 · Proposal & Quote</p>
                        {data.quote && (
                            <div className="rounded-xl bg-cafe-latte/40 p-4 mb-4">
                                {(data.quote.items || []).map((it, i) => (
                                    <div key={i} className="flex justify-between text-[13px] py-1.5">
                                        <span className="text-cafe-espresso">{it.name}{it.qty > 1 ? ` × ${it.qty}` : ""}</span>
                                        <span className="text-cafe-mocha">RM {(it.qty * it.unit_price).toLocaleString()}</span>
                                    </div>))}
                                {Number(data.quote.discount) > 0 && (
                                    <div className="flex justify-between text-[13px] py-1.5 text-[#B0503C]">
                                        <span>折扣 Discount</span><span>− RM {Number(data.quote.discount).toLocaleString()}</span></div>)}
                                <div className="flex justify-between border-t border-cafe-espresso/15 mt-2 pt-2.5">
                                    <b className="text-[13.5px] text-cafe-espresso">总计 Total</b>
                                    <span className="font-serif text-lg text-cafe-olive" data-testid="portal-quote-total">RM {Number(data.quote.total || 0).toLocaleString()}</span>
                                </div>
                            </div>)}
                        {data.proposal_path && (
                            <a href={data.proposal_path} target="_blank" rel="noreferrer" data-testid="portal-proposal-link"
                                className="inline-flex items-center gap-2 rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream px-5 py-2.5 text-[13px] font-medium">
                                <Star className="h-3.5 w-3.5" /> 查看完整增长提案 · View Full Proposal
                            </a>)}
                    </section>)}
                <p className="text-[11px] text-cafe-mocha/60 text-center mt-4">
                    Kaily F&B Visual Studio · 有任何问题请直接 WhatsApp 我们</p>
            </main>
        </div>
    );
}
